/**
 * @file ResourceViewerPage.js
 * @description Full-page viewer for training resources with progress tracking.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFileCategory } from '../../helpers/FileUtils';
import {
    fetchAssetByID,
    updateResourceProgress,
} from '../../../services/clientService';
import { ROUTES } from '../../../constants/routes';
import styles from '../AssetLibrary.module.scss';
import Spinner from '../../ui/spinners/Spinner';
import RichTextContent from '../../ui/RichTextContent';
import { getText } from '../../../i18n';

const PROGRESS_REPORT_INTERVAL_SECONDS = 10;

const ResourceViewerPage = () => {
    const { assetId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [asset, setAsset] = useState(location.state?.asset || null);
    const [isLoading, setIsLoading] = useState(!location.state?.asset);
    const [showTranscript, setShowTranscript] = useState(false);

    const mediaRef = useRef(null);
    const lastReportedSecondRef = useRef(0);
    const hasResumedRef = useRef(false);
    const hasReportedOpenRef = useRef(false);

    const returnTo = location.state?.returnTo;

    useEffect(() => {
        const loadAsset = async () => {
            if (asset) return;

            try {
                setIsLoading(true);
                const fetchedAsset = await fetchAssetByID(
                    assetId,
                    null,
                    false,
                    true,
                );
                setAsset(fetchedAsset);
            } catch (error) {
                console.error('Failed to load resource asset', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadAsset();
    }, [asset, assetId]);

    const fileCategory = useMemo(
        () => getFileCategory(asset?.fileType),
        [asset?.fileType],
    );

    const isPlayableMedia =
        fileCategory === 'video' || fileCategory === 'audio';

    const consumeOpenedFlag = () => {
        if (hasReportedOpenRef.current) return false;
        hasReportedOpenRef.current = true;
        return true;
    };

    const reportProgress = async progress => {
        if (!asset?.id) return;

        try {
            const updatedProgress = await updateResourceProgress(
                asset.id,
                progress,
            );
            setAsset(prev =>
                prev ? { ...prev, resourceProgress: updatedProgress } : prev,
            );
        } catch (error) {
            console.error('Failed to update resource progress', error);
        }
    };

    useEffect(() => {
        if (!asset?.id || isPlayableMedia || hasReportedOpenRef.current) {
            return;
        }

        hasReportedOpenRef.current = true;
        reportProgress({ opened: true });
    }, [asset?.id, isPlayableMedia]);

    const resumePlaybackPosition = () => {
        const resumeSeconds = asset?.resourceProgress?.resumePositionSeconds;
        if (hasResumedRef.current || !resumeSeconds || !mediaRef.current)
            return;

        hasResumedRef.current = true;
        mediaRef.current.currentTime = resumeSeconds;
    };

    const handleTimeUpdate = () => {
        const media = mediaRef.current;
        if (!media || !Number.isFinite(media.duration) || media.duration <= 0) {
            return;
        }

        const currentSecond = Math.floor(media.currentTime);
        if (
            currentSecond - lastReportedSecondRef.current <
            PROGRESS_REPORT_INTERVAL_SECONDS
        ) {
            return;
        }

        lastReportedSecondRef.current = currentSecond;
        reportProgress({
            opened: consumeOpenedFlag(),
            progressPercent: Math.min(
                100,
                Math.round((media.currentTime / media.duration) * 100),
            ),
            resumePositionSeconds: currentSecond,
        });
    };

    const handleEnded = () => {
        const media = mediaRef.current;
        lastReportedSecondRef.current = 0;
        reportProgress({
            opened: consumeOpenedFlag(),
            progressPercent: 100,
            resumePositionSeconds: media ? Math.floor(media.duration) : null,
        });
    };

    const handleClose = () => {
        if (returnTo) {
            navigate(returnTo);
            return;
        }

        navigate(ROUTES.RESOURCES);
    };

    if (isLoading || !asset) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    const mediaProps = {
        ref: mediaRef,
        src: asset.url,
        controls: true,
        autoPlay: true,
        onLoadedMetadata: resumePlaybackPosition,
        onTimeUpdate: handleTimeUpdate,
        onEnded: handleEnded,
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className={`card-body ${styles.resourceViewerPage}`}>
                    <button
                        type="button"
                        className={`ing-text-buttons ${styles.resourceViewerClose}`}
                        onClick={handleClose}
                        aria-label={getText('msg_86442b61e1bc')}
                    >
                        <i className="bi bi-x me-2" aria-hidden="true" />
                        {getText('msg_bbfa773e5a63')}
                    </button>

                    <p className="page-title card-title">{asset.name}</p>

                    <p className="ing-assets-subtitle mt-2">
                        {getText('msg_c86e3dd52fba')}
                        <br />
                        {getText('msg_c86e3dd52fbb')}
                    </p>

                    <div className={styles.resourceViewerMedia}>
                        {fileCategory === 'video' && (
                            <video {...mediaProps} playsInline />
                        )}
                        {fileCategory === 'audio' && <audio {...mediaProps} />}
                        {fileCategory === 'image' && (
                            <img src={asset.url} alt={asset.name} />
                        )}
                        {fileCategory === 'document' && (
                            <iframe
                                title={asset.name}
                                src={asset.url}
                                className={styles.resourceViewerDocument}
                            />
                        )}
                    </div>

                    <div className={styles.resourceViewerDetails}>
                        {isPlayableMedia && (
                            <strong>
                                {getText('msg_3dc9675c6b13', {
                                    assetName: asset.name,
                                })}
                            </strong>
                        )}

                        {asset.transcript && (
                            <button
                                type="button"
                                className={
                                    styles.resourceViewerTranscriptButton
                                }
                                onClick={() =>
                                    setShowTranscript(!showTranscript)
                                }
                            >
                                {showTranscript
                                    ? getText('msg_53025e53f0f0')
                                    : getText('msg_f334cf9b3838')}
                            </button>
                        )}

                        {(showTranscript
                            ? asset.transcript
                            : asset.overview) && (
                            <div className={styles.resourceViewerOverview}>
                                <RichTextContent
                                    content={
                                        showTranscript
                                            ? asset.transcript
                                            : asset.overview
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResourceViewerPage;
